import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  SegmentedFilter,
} from "@assetwatch/ui";
import { useBlocker } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { AssetId, WorkRequestId } from "../../../lib/workIds";
import { useAssessWorkRequestMutation } from "../../../mutations/useAssessWorkRequestMutation";
import { useCreateWorkRequestMutation } from "../../../mutations/useCreateWorkRequestMutation";
import { useWorkRequestFiles } from "../../../queries/useWorkRequestFiles";
import type {
  DuplicateCheckMatch,
  SelectableRequestPriority,
  WorkRequestDraft,
} from "../../../types/workRequest";
import { UnsavedChangesDialog } from "../../UnsavedChangesDialog";
import { type AiNotice, AiNoticeAlert } from "./AiNoticeAlert";
import {
  type StagedAttachment,
  selectAttachments,
} from "./attachmentSelection";
import { GuidedChatComposer } from "./GuidedChatControls";
import {
  AssetCandidates,
  AssistantTypingIndicator,
  getReviewAssetLabel,
  IntakeTranscript,
} from "./NewWorkRequestChat";
import { NewWorkRequestForm } from "./NewWorkRequestForm";
import { ReliAssessmentProgress } from "./ReliAssessmentProgress";
import { RequestAssessment } from "./RequestAssessment";
import {
  type AttachmentResult,
  RequestSubmittedView,
} from "./RequestSubmittedView";
import { useNewWorkRequestIntake } from "./useNewWorkRequestIntake";

type NewWorkRequestSideSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewRequests?: (requestId: WorkRequestId) => void;
  initialAssetId?: AssetId | null;
  initialAssetName?: string | null;
  /** Simulated minimum duration of the "Reviewing request…" assessment step.
   * Overridable so tests can skip the real-time pacing. */
  assessmentMinDurationMs?: number;
};

type SheetView = "compose" | "assessing" | "assessment" | "success";
type ComposeTab = "chat" | "form";
const MIN_ASSESSMENT_DURATION_MS = 2700;

export function NewWorkRequestSideSheet({
  initialAssetId = null,
  initialAssetName = null,
  open,
  onOpenChange,
  onViewRequests,
  assessmentMinDurationMs = MIN_ASSESSMENT_DURATION_MS,
}: NewWorkRequestSideSheetProps) {
  const [view, setView] = useState<SheetView>("compose");
  const [activeTab, setActiveTab] = useState<ComposeTab>("chat");
  const [createdRequestId, setCreatedRequestId] =
    useState<WorkRequestId | null>(null);
  const [createdRequestNumber, setCreatedRequestNumber] = useState<
    string | null
  >(null);
  const [duplicateMatches, setDuplicateMatches] = useState<
    DuplicateCheckMatch[]
  >([]);
  const [duplicateCheckFailed, setDuplicateCheckFailed] = useState(false);
  const [suggestedPriority, setSuggestedPriority] =
    useState<SelectableRequestPriority | null>(null);
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
  const [attachmentErrors, setAttachmentErrors] = useState<string[]>([]);
  const [attachmentResults, setAttachmentResults] = useState<
    AttachmentResult[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiNotice, setAiNotice] = useState<AiNotice | null>(null);
  const [isDiscardPromptOpen, setIsDiscardPromptOpen] = useState(false);
  // Set while a route change is held for the discard prompt; resolves true to
  // block the navigation and false to let it through.
  const pendingNavigationRef = useRef<((shouldBlock: boolean) => void) | null>(
    null,
  );
  const createMutation = useCreateWorkRequestMutation();
  const assessMutation = useAssessWorkRequestMutation();
  const { attachFile, prepareUpload } = useWorkRequestFiles();
  const intake = useNewWorkRequestIntake({
    initialAssetId,
    initialAssetName,
    onAssessment: runAssessment,
    onClearNotice: () => setAiNotice(null),
    onRouteToForm: routeToForm,
  });
  const {
    chatEntries,
    chatPhase,
    handleCandidate,
    handleSend,
    intakeState,
    isPending: intakeIsPending,
    message,
    reset: resetIntake,
    setIntakeState,
    setMessage,
  } = intake;
  const isPending =
    intakeIsPending ||
    createMutation.isPending ||
    assessMutation.isPending ||
    isSubmitting;
  const canReview = Boolean(
    intakeState.draft.assetId &&
      intakeState.draft.workRequestTitle?.trim() &&
      intakeState.draft.workRequestDescription?.trim(),
  );
  const { draft } = intakeState;
  // Anything the requester has entered that closing would throw away. An
  // asset passed in from Asset context is not the requester's own input.
  const isDirty =
    view !== "success" &&
    (message.trim().length > 0 ||
      chatEntries.length > 1 ||
      (draft.workRequestTitle ?? "").trim().length > 0 ||
      (draft.workRequestDescription ?? "").trim().length > 0 ||
      draft.assetId !== initialAssetId ||
      draft.priorityCode !== null ||
      (draft.categoryIds ?? []).length > 0 ||
      attachments.length > 0);
  const assessmentTimeoutRef = useRef<number | null>(null);
  // Bumped whenever an assessment starts or the draft is reset. A callback
  // or scheduled timeout from an older generation is stale and must not
  // touch state — otherwise a late response for a discarded draft could
  // resurrect its matches/priority after the user reopens the sheet.
  const assessmentGenerationRef = useRef(0);

  function clearAssessmentTimeout() {
    if (assessmentTimeoutRef.current !== null) {
      window.clearTimeout(assessmentTimeoutRef.current);
      assessmentTimeoutRef.current = null;
    }
  }

  useEffect(() => {
    return () => {
      if (assessmentTimeoutRef.current !== null) {
        window.clearTimeout(assessmentTimeoutRef.current);
      }
    };
  }, []);

  function reset() {
    resetIntake();
    clearAssessmentTimeout();
    assessmentGenerationRef.current += 1;
    setView("compose");
    setActiveTab("chat");
    setCreatedRequestId(null);
    setCreatedRequestNumber(null);
    setDuplicateMatches([]);
    setDuplicateCheckFailed(false);
    setSuggestedPriority(null);
    setAttachments([]);
    setAttachmentErrors([]);
    setAttachmentResults([]);
    setIsSubmitting(false);
    setAiNotice(null);
    setIsDiscardPromptOpen(false);
  }

  function addAttachments(selected: File[]) {
    const { accepted, errors } = selectAttachments(
      attachments.length,
      selected,
    );
    setAttachments((current) => [
      ...current,
      ...accepted.map((file) => ({ id: crypto.randomUUID(), file })),
    ]);
    setAttachmentErrors(errors);
  }

  function removeAttachment(id: string) {
    setAttachments((current) =>
      current.filter((attachment) => attachment.id !== id),
    );
    setAttachmentErrors([]);
  }

  // Closes the sheet and throws the draft away. The pre-submit assessment is
  // best effort and can be slow or hang — never let it alone block closing
  // the sheet. Intake/create/attachment work stays in flight, since losing
  // those responses mid-flight would be confusing (a bubble, created request,
  // or attachment error could land after close).
  const hasWorkInFlight =
    intakeIsPending || createMutation.isPending || isSubmitting;

  function closeAndReset() {
    if (hasWorkInFlight) return;
    reset();
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (isDirty) {
      if (hasWorkInFlight) return;
      setIsDiscardPromptOpen(true);
      return;
    }
    closeAndReset();
  }

  function keepEditing() {
    setIsDiscardPromptOpen(false);
    const resolve = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    resolve?.(true);
  }

  function confirmDiscard() {
    // Work that started after the prompt opened would land on a discarded
    // draft, so treat this as keeping the request.
    if (hasWorkInFlight) {
      keepEditing();
      return;
    }
    const resolve = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setIsDiscardPromptOpen(false);
    reset();
    onOpenChange(false);
    resolve?.(false);
  }

  // Leaving the host screen would unmount the sheet and lose the draft, so
  // hold route changes behind the same prompt while there is something to lose.
  const isGuardingNavigation = open && isDirty;
  useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (!isGuardingNavigation) return false;
      // Search-only changes stay on the same screen and keep the sheet alive.
      if (current.pathname === next.pathname) return false;
      // In-flight responses would land on a discarded draft, so hold the
      // navigation without offering a discard.
      if (hasWorkInFlight) return true;
      return new Promise<boolean>((resolve) => {
        pendingNavigationRef.current?.(true);
        pendingNavigationRef.current = resolve;
        setIsDiscardPromptOpen(true);
      });
    },
    enableBeforeUnload: () => isGuardingNavigation,
    disabled: !isGuardingNavigation,
  });

  useEffect(() => {
    if (!isGuardingNavigation) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isGuardingNavigation]);

  // Runs the combined asset-match/duplicate-scan/priority-score assessment
  // and gates the transition to the assessment screen on whichever finishes
  // last: the simulated progress pacing or the real network call.
  function runAssessment(draft: WorkRequestDraft) {
    const { assetId, workRequestTitle, workRequestDescription } = draft;
    if (
      !assetId ||
      !workRequestTitle?.trim() ||
      !workRequestDescription?.trim()
    ) {
      // Bedrock can leave required fields (such as the title) empty  -  send
      // the user to the form to fill the gaps instead of showing an
      // assessment screen whose submit cannot succeed, and say why.
      routeToForm("incomplete");
      return;
    }

    setView("assessing");
    const startedAt = Date.now();
    assessmentGenerationRef.current += 1;
    const generation = assessmentGenerationRef.current;
    assessMutation.mutate(
      { assetId, title: workRequestTitle, description: workRequestDescription },
      {
        onSuccess: (response) => {
          if (assessmentGenerationRef.current !== generation) return;
          // The scored priority is only a suggestion; it must never fill or
          // overwrite the requester's own (possibly empty) choice.
          setDuplicateCheckFailed(false);
          setDuplicateMatches(response.matches);
          setSuggestedPriority(response.priorityCode);
        },
        // Fail open: an unavailable assessment must never block submission,
        // but the requester is told the duplicate check did not run so an
        // empty list is not read as "no duplicates".
        onError: () => {
          if (assessmentGenerationRef.current !== generation) return;
          setDuplicateCheckFailed(true);
          setDuplicateMatches([]);
          setSuggestedPriority(null);
        },
        onSettled: () => {
          if (assessmentGenerationRef.current !== generation) return;
          const remaining = Math.max(
            0,
            assessmentMinDurationMs - (Date.now() - startedAt),
          );
          clearAssessmentTimeout();
          assessmentTimeoutRef.current = window.setTimeout(() => {
            assessmentTimeoutRef.current = null;
            if (assessmentGenerationRef.current !== generation) return;
            setView("assessment");
          }, remaining);
        },
      },
    );
  }

  // Moves the requester to the form with the AI notice; the captured draft
  // lives in intakeState, so the form is prefilled with it.
  function routeToForm(notice: AiNotice) {
    setAiNotice(notice);
    setActiveTab("form");
    setView("compose");
  }

  function handleFormReview() {
    const { assetId, workRequestDescription, workRequestTitle } =
      intakeState.draft;
    if (
      !assetId ||
      !workRequestDescription?.trim() ||
      !workRequestTitle?.trim()
    )
      return;
    setAiNotice(null);
    setIntakeState((current) => ({
      ...current,
      stage: "review",
      readyToCreate: true,
    }));
    runAssessment(intakeState.draft);
  }

  function submitCreate() {
    if (isSubmitting) return;
    const { assetId, priorityCode, workRequestTitle, workRequestDescription } =
      intakeState.draft;
    if (!assetId || !workRequestDescription || !workRequestTitle) return;
    setIsSubmitting(true);
    // Only a REQUEST-type match maps to a WorkRequestID — a WORK_ORDER match
    // has nothing valid to link here. Carries the top pre-submit match
    // forward so it's available if this request is later declined as a
    // duplicate, even though the requester chose to submit anyway.
    const duplicateRequest = duplicateMatches.find(
      (match): match is DuplicateCheckMatch & { type: "REQUEST" } =>
        match.type === "REQUEST",
    );
    const duplicateOfWorkRequestId = duplicateRequest?.id ?? null;
    createMutation.mutate(
      {
        assetId,
        duplicateOfWorkRequestId,
        categoryIds: intakeState.draft.categoryIds ?? [],
        priorityCode,
        workRequestDescription,
        workRequestTitle,
      },
      {
        onSuccess: async (response) => {
          setCreatedRequestId(response.workRequestId);
          setCreatedRequestNumber(response.requestNumber);
          try {
            const results: AttachmentResult[] = [];
            for (const { id, file } of attachments) {
              try {
                const uploadId = await prepareUpload({
                  requestId: response.workRequestId,
                  file,
                });
                await attachFile.mutateAsync({
                  requestId: response.workRequestId,
                  uploadId,
                });
                results.push({ id, name: file.name, status: "uploaded" });
              } catch {
                // One bad file must not stop the remaining attachments.
                results.push({ id, name: file.name, status: "failed" });
              }
            }
            setAttachmentResults(results);
          } finally {
            setIsSubmitting(false);
            setView("success");
          }
        },
        onError: () => setIsSubmitting(false),
      },
    );
  }

  function continueWithForm() {
    // The captured draft lives in intakeState, so switching tabs keeps it.
    setActiveTab("form");
  }

  const reviewAssetLabel = getReviewAssetLabel(
    chatEntries,
    intakeState.draft.assetName,
  );

  return (
    <>
      <Drawer
        onOpenChange={handleOpenChange}
        open={open}
        responsive={false}
        side="right"
      >
        <DrawerContent size="small">
          {view === "success" && createdRequestId && createdRequestNumber ? (
            <RequestSubmittedView
              attachmentResults={attachmentResults}
              onSubmitAnother={reset}
              onViewRequests={() => {
                onViewRequests?.(createdRequestId);
                handleOpenChange(false);
              }}
              requestNumber={createdRequestNumber}
            />
          ) : null}

          {view === "assessing" ? (
            <>
              <DrawerHeader
                closeLabel="Close New Request"
                title="New Request"
              />
              <ReliAssessmentProgress
                isComplete={!assessMutation.isPending}
                minDurationMs={assessmentMinDurationMs}
              />
            </>
          ) : null}

          {view === "assessment" ? (
            <>
              <DrawerHeader
                closeLabel="Close New Request"
                title="New Request"
              />
              <RequestAssessment
                asset={reviewAssetLabel}
                attachments={attachments.map(({ id, file }) => ({
                  id,
                  name: file.name,
                }))}
                canSubmit={canReview}
                description={intakeState.draft.workRequestDescription}
                duplicateCheckUnavailable={duplicateCheckFailed}
                isPending={isPending}
                matches={duplicateMatches}
                onDiscard={closeAndReset}
                onEdit={() => {
                  setActiveTab("form");
                  setView("compose");
                }}
                onSubmit={submitCreate}
                priority={intakeState.draft.priorityCode}
                suggestedPriority={suggestedPriority}
                title={intakeState.draft.workRequestTitle}
              />
            </>
          ) : null}

          {view === "compose" ? (
            <>
              <DrawerHeader
                closeLabel="Close New Request"
                title="New Request"
              />
              <div className="shrink-0 px-(--spacing-20)">
                <SegmentedFilter
                  aria-label="Request entry method"
                  items={[
                    { label: "Chat", value: "chat" },
                    { label: "Form", value: "form" },
                  ]}
                  onValueChange={(value) => {
                    setActiveTab(value === "form" ? "form" : "chat");
                  }}
                  value={activeTab}
                />
              </div>

              {activeTab === "chat" ? (
                <>
                  <div className="min-h-0 flex-1 overflow-y-auto max-[904px]:pb-[env(safe-area-inset-bottom)]">
                    <div
                      aria-live="polite"
                      className="flex min-h-full flex-col gap-(--spacing-16) bg-surface-container-low p-(--spacing-20)"
                    >
                      <IntakeTranscript entries={chatEntries} />
                      {aiNotice === "unavailable" ? (
                        <AiNoticeAlert
                          notice="unavailable"
                          onContinue={continueWithForm}
                        />
                      ) : null}
                      {chatPhase === "resolve_asset" &&
                      intakeState.assetCandidates.length > 0 ? (
                        <AssetCandidates
                          candidates={intakeState.assetCandidates}
                          disabled={isPending}
                          onSelect={handleCandidate}
                        />
                      ) : null}
                      {intakeIsPending ? <AssistantTypingIndicator /> : null}
                      <div
                        ref={(node) =>
                          node?.scrollIntoView?.({ block: "nearest" })
                        }
                      />
                    </div>
                  </div>
                  <DrawerFooter>
                    <GuidedChatComposer
                      disabled={isPending}
                      onChange={setMessage}
                      onSend={handleSend}
                      value={message}
                    />
                  </DrawerFooter>
                </>
              ) : (
                <NewWorkRequestForm
                  aiNotice={aiNotice}
                  attachmentErrors={attachmentErrors}
                  attachments={attachments}
                  canReview={canReview}
                  draft={intakeState.draft}
                  onAddAttachments={addAttachments}
                  onAssetChange={(assetId, assetName, assetKind) =>
                    setIntakeState((current) => ({
                      ...current,
                      draft: {
                        ...current.draft,
                        assetId,
                        assetName,
                        assetKind,
                      },
                    }))
                  }
                  onDescriptionChange={(workRequestDescription) =>
                    setIntakeState((current) => ({
                      ...current,
                      draft: { ...current.draft, workRequestDescription },
                    }))
                  }
                  onPriorityChange={(priorityCode) =>
                    setIntakeState((current) => ({
                      ...current,
                      draft: { ...current.draft, priorityCode },
                    }))
                  }
                  onRemoveAttachment={removeAttachment}
                  onReview={handleFormReview}
                  onTitleChange={(workRequestTitle) =>
                    setIntakeState((current) => ({
                      ...current,
                      draft: {
                        ...current.draft,
                        workRequestTitle,
                        titleIsFallback: false,
                      },
                    }))
                  }
                  suggestedPriority={suggestedPriority}
                />
              )}
            </>
          ) : null}
        </DrawerContent>
      </Drawer>
      <UnsavedChangesDialog
        description="Your changes will be lost."
        onDiscard={confirmDiscard}
        onKeepEditing={keepEditing}
        open={isDiscardPromptOpen}
        title="Discard this request?"
      />
    </>
  );
}
